import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  resourceLastUpdatedAvailable,
  StaleResourceType,
} from '../../features/stale_resource_detection/stale_resource_detection_slice';
import {
  useLazyGetDbPlannerWeekByIDQuery,
  useLazyGetProgrammeQuery,
  useLazyGetSharedContentBoardIAMQuery,
  useLazyGetSmorgBoardQuery,
} from '../../app/smorgAppSyncGraphQLApi';

const ResourceStatusControl = ({ resourceType, resourceIDs }) => {
  const dispatch = useDispatch();

  const memoizedResourceIDs = useMemo(
    () => resourceIDs,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(resourceIDs)],
  );

  const [lazyGetDbPlannerWeekByID] = useLazyGetDbPlannerWeekByIDQuery();

  const [lazyGetSmorgBoard] = useLazyGetSmorgBoardQuery();

  const [lazyGetSharedContentBoardIAM] = useLazyGetSharedContentBoardIAMQuery();

  const [lazyGetProgramme] = useLazyGetProgrammeQuery();

  const getResource = useCallback(
    async (resourceID) => {
      if (!resourceID) {
        return null;
      }
      // console.log(`Getting resource ${resourceType} with ID ${resourceID}`);
      if (resourceType === StaleResourceType.PlannerWeek) {
        const result = lazyGetDbPlannerWeekByID(resourceID);
        result.refetch();
        const { data } = await result;
        return data;
      }
      if (resourceType === StaleResourceType.SmorgBoard) {
        const result = lazyGetSmorgBoard(resourceID);
        result.refetch();
        const { data } = await result;
        return data;
      }
      if (resourceType === StaleResourceType.SharedContentBoard) {
        const result = lazyGetSharedContentBoardIAM(resourceID);
        result.refetch();
        const { data } = await result;
        return data;
      }
      if (resourceType === StaleResourceType.Programme) {
        const result = lazyGetProgramme(resourceID);
        result.refetch();
        const { data } = await result;
        return data;
      }

      return Promise.reject(
        new Error(`Unsupported resource type: ${resourceType}`),
      );
    },
    [
      lazyGetDbPlannerWeekByID,
      lazyGetProgramme,
      lazyGetSharedContentBoardIAM,
      lazyGetSmorgBoard,
      resourceType,
    ],
  );

  const cancelResourceStatusControl = () => {
    if (window._gResourceStatusInterval) {
      console.log('Clearing interval for resource status control');
      clearInterval(window._gResourceStatusInterval);
      window._gResourceStatusInterval = null;
    }
  };

  const resourceStatusControlLoop = useCallback(() => {
    const checkRefreshNeeded = async (resourceID) => {
      const resource = await getResource(resourceID);
      // console.log(resource);
      if (resource) {
        dispatch(
          resourceLastUpdatedAvailable({
            resourceType,
            resourceID,
            updatedAt: resource.updatedAt,
          }),
        );
      }
    };

    if (!window._gResourceStatusInterval) {
      (memoizedResourceIDs || []).forEach((resourceID) =>
        checkRefreshNeeded(resourceID),
      );
      console.log(
        `Setting interval for resource status control ${resourceType} ${JSON.stringify(
          memoizedResourceIDs,
        )}`,
      );
      window._gResourceStatusInterval = setInterval(async () => {
        // console.log(`Tick ${memoizedResourceIDs}`);
        await Promise.all(
          (memoizedResourceIDs || []).map((resourceID) =>
            checkRefreshNeeded(resourceID),
          ),
        );
        // Reload after 30 seconds. Note, refetching seems to miss
        // one cycle, so the expected refresh timeout is 60 seconds.
      }, 0.5 * 60 * 1000);
    }
  }, [getResource, resourceType, dispatch, memoizedResourceIDs]);

  useEffect(() => {
    resourceStatusControlLoop();
    return () => {
      cancelResourceStatusControl();
    };
  }, [resourceStatusControlLoop]);

  return null;
};

ResourceStatusControl.propTypes = {
  resourceType: PropTypes.string.isRequired,
  resourceIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default ResourceStatusControl;
